import { useFormikContext } from 'formik';
import { FC } from 'react';
import { useTranslation } from 'react-i18next';
import { DaySelector } from 'src/components/DaySelector';
import { Grid } from 'src/components/Grid';
import { Stack } from 'src/components/Stack';
import { Typography } from 'src/components/Typography';
import { FormAutocomplete } from 'src/shared/Forms/Fields/FormAutocomplete';
import { FormTextField } from 'src/shared/Forms/Fields/FormTextField';

import {
  type RecurringType,
  buildMonthCalendarDateOptions,
  buildMonthWeekOccurrenceOptions,
  buildRecurringTypeOptions,
} from './recurringRuleFieldConstants';

export interface RecurringRuleFieldNames {
  recurringCount: string;
  recurringType: string;
  recurringDayNumbers: string;
  days: string;
}

export interface RecurringRuleFieldsProps {
  fieldNames: RecurringRuleFieldNames;
  allowedRecurringTypes?: RecurringType[];
  allowedDays?: number[];
  /**
   * When provided, only these day-number ids are offered in the
   * month_on_date / month_on_day autocompletes. Used by the membership
   * dialog to constrain choices to the parent session's recurringDayNumbers.
   */
  allowedDayNumbers?: number[];
  /**
   * Day-of-week index (0-6) to pre-fill when switching to a type that shows
   * the day selector from one that does not. Session passes
   * `getDay(values.startTime)`; membership can omit if not needed.
   */
  defaultDay?: number;
  /**
   * When set, that weekday cannot be deselected in the day selector (edit
   * session instance). See `DaySelector` `lockedDay`.
   */
  lockedDay?: number;
  /**
   * When true, the recurring count and type fields are disabled. Used by the
   * membership dialog so the recurrence pattern always matches the parent
   * scheduled appointment.
   */
  readOnly?: boolean;
}

const TYPES_WITH_DAY_SELECTOR: RecurringType[] = ['week', 'month_on_day'];

/**
 * Shared recurring-rule fields matching the v2 `recurring-options.hbs` partial.
 * Renders inside an existing Formik context; parents own the form provider and
 * submit handlers. Translations come from the canonical
 * `calendar.editEventDialog.recurringSettings` namespace.
 */
export const RecurringRuleFields: FC<RecurringRuleFieldsProps> = ({
  fieldNames,
  allowedRecurringTypes,
  allowedDays,
  allowedDayNumbers,
  defaultDay,
  lockedDay,
  readOnly = false,
}) => {
  const { t } = useTranslation('translation', {
    keyPrefix: 'calendar.editEventDialog.recurringSettings',
  });
  const { values, setFieldValue } = useFormikContext<Record<string, any>>();

  const recurringCount: number = values[fieldNames.recurringCount] ?? 1;
  const recurringType: RecurringType = values[fieldNames.recurringType] ?? 'week';
  const daysValue: number[] = values[fieldNames.days] ?? [];

  const recurringTypeOptions = buildRecurringTypeOptions(
    recurringCount,
    t,
    allowedRecurringTypes
  );

  const allDateOptions = buildMonthCalendarDateOptions(t);
  const allWeekOccurrenceOptions = buildMonthWeekOccurrenceOptions(t);

  const hasDayNumberRestriction =
    allowedDayNumbers && allowedDayNumbers.length > 0;
  const monthCalendarDateOptions = hasDayNumberRestriction
    ? allDateOptions.filter((o) => allowedDayNumbers.includes(o.id))
    : allDateOptions;
  const monthWeekOccurrenceOptions = hasDayNumberRestriction
    ? allWeekOccurrenceOptions.filter((o) => allowedDayNumbers.includes(o.id))
    : allWeekOccurrenceOptions;

  const handleRecurringTypeChange = (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const prevType = recurringType;
    const nextType = event.target.value as RecurringType;

    if (nextType === 'month_on_day' || nextType === 'month_on_date') {
      setFieldValue(fieldNames.recurringDayNumbers, [1]);
    } else {
      setFieldValue(fieldNames.recurringDayNumbers, []);
    }

    if (!TYPES_WITH_DAY_SELECTOR.includes(nextType)) {
      setFieldValue(fieldNames.days, []);
    } else if (
      TYPES_WITH_DAY_SELECTOR.includes(nextType) &&
      !TYPES_WITH_DAY_SELECTOR.includes(prevType)
    ) {
      setFieldValue(
        fieldNames.days,
        defaultDay !== undefined ? [defaultDay] : []
      );
    }
  };

  return (
    <Stack spacing={3}>
      <Typography variant="body2" color="text.secondary">
        {t('repeatsEvery')}
      </Typography>
      <Grid container spacing={2}>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            name={fieldNames.recurringCount}
            label={t('intervalCount')}
            type="number"
            placeholder="1"
            disabled={readOnly}
          />
        </Grid>
        <Grid size={{ xs: 12, sm: 6 }}>
          <FormTextField
            name={fieldNames.recurringType}
            label={t('intervalUnit')}
            options={recurringTypeOptions}
            onChange={handleRecurringTypeChange}
            disabled={readOnly}
          />
        </Grid>
      </Grid>

      {recurringType === 'week' && (
        <DaySelector
          label={t('repeatOn')}
          value={daysValue}
          onChange={(days) => setFieldValue(fieldNames.days, days)}
          allowedDays={allowedDays}
          lockedDay={lockedDay}
        />
      )}

      {recurringType === 'month_on_date' && (
        <FormAutocomplete
          name={fieldNames.recurringDayNumbers}
          label={t('datesOfMonth')}
          options={monthCalendarDateOptions}
          multiple
          getOptionLabel={(option: number | { id: number; name: string }) => {
            const id = typeof option === 'number' ? option : option.id;
            const found = allDateOptions.find((d) => d.id === id);
            return found?.name ?? '';
          }}
          isOptionEqualToValue={(option, value) => {
            const optionId =
              typeof option === 'number' ? option : option.id;
            const valueId =
              typeof value === 'number' ? value : value.id;
            return optionId === valueId;
          }}
          transform={(value: unknown[]) =>
            value.map((v) =>
              typeof v === 'number' ? v : (v as { id: number }).id
            )
          }
          placeholder={t('selectDatesPlaceholder')}
          limitTags={3}
        />
      )}

      {recurringType === 'month_on_day' && (
        <Stack spacing={2}>
          <FormAutocomplete
            name={fieldNames.recurringDayNumbers}
            label={t('weekOrdinalInMonth')}
            options={monthWeekOccurrenceOptions}
            multiple
            getOptionLabel={(option: number | { id: number; name: string }) => {
              const id = typeof option === 'number' ? option : option.id;
              const found = allWeekOccurrenceOptions.find(
                (d) => d.id === id
              );
              return found?.name ?? '';
            }}
            isOptionEqualToValue={(option, value) => {
              const optionId =
                typeof option === 'number' ? option : option.id;
              const valueId =
                typeof value === 'number' ? value : value.id;
              return optionId === valueId;
            }}
            transform={(value: unknown[]) =>
              value.map((v) =>
                typeof v === 'number' ? v : (v as { id: number }).id
              )
            }
            placeholder={t('anyWeekOrdinalPlaceholder')}
            limitTags={2}
          />
          <DaySelector
            label={t('repeatOn')}
            value={daysValue}
            onChange={(days) => setFieldValue(fieldNames.days, days)}
            allowedDays={allowedDays}
            lockedDay={lockedDay}
          />
        </Stack>
      )}
    </Stack>
  );
};
